import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { RootState } from '@root/app.state';
import { selectLoadedMobileUpload, selectMobileUploadUpload } from '@core/core.selectors';
import { filter, map, tap } from 'rxjs/operators';
import { ResourceStatus } from '@platform/shared/resource.state';
import { MobileUploadDto } from '@core/mobile-upload/mobile-upload.model';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { areFilesValid, getFileExtension, getFileNameWithoutExtension } from '@platform/shared/utilities/file-utility';
import { v4 as uuid } from 'uuid';
import { selectPortalConfig } from '@platform/platform.selectors';
import { PortalConfig } from '@platform/platform.state';
import { ToastService } from '@platform/toast/toast.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as CoreActions from '../core.actions';

interface MobileUploadFile {
  id: string;
  name: string;
  extension: string;
  file: File;
}

@Component({
  selector: 'app-mobile-upload',
  templateUrl: './mobile-upload.component.html',
  styleUrls: ['./mobile-upload.component.scss'],
  standalone: false,
})
export class MobileUploadComponent implements OnInit, OnDestroy {
  mobileUpload: MobileUploadDto = null!;
  files: MobileUploadFile[] | null = null;
  uploadPending = false;
  formGroup: FormGroup = null!;
  invalidFileAndFolderNameCharacters: string[] = null!;
  allowedFileFormats: string[] = null!;

  constructor(
    private store$: Store<RootState>,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({});
    this.formGroup.valueChanges.pipe(tap((c) => console.log(c)));

    this.store$
      .pipe(
        select(selectPortalConfig),
        filter((config): config is PortalConfig => !!config),
      )
      .subscribe((config) => (this.invalidFileAndFolderNameCharacters = config.invalidFileAndFolderNameCharacters));

    this.store$
      .pipe(
        select(selectMobileUploadUpload),
        map((m) => m.status),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((status) => this.onMobileUploadStatusChange(status));

    this.store$
      .pipe(
        select(selectLoadedMobileUpload),
        filter((uploadResource) => uploadResource.status === 'SUCCEEDED'),
        map((uploadResource) => uploadResource.data),
      )
      .subscribe((mobileUpload) => (this.mobileUpload = mobileUpload));
  }

  ngOnDestroy(): void {
    this.resetForm();
  }

  onFileSelected(event: Event): void {
    const fileList = (event.target as HTMLInputElement).files;
    if (!fileList) {
      this.files = null;
      return;
    }

    const fileValidation = areFilesValid(fileList, this.invalidFileAndFolderNameCharacters, this.mobileUpload.allowedFileUploadFormats);
    if (!fileValidation.valid) {
      this.toastService.showErrorToast(fileValidation.error.translationKey, fileValidation.error.interpolateParams);
      return;
    }

    this.files = Array.from(fileList).map((file) => ({
      id: uuid(),
      name: getFileNameWithoutExtension(file.name),
      extension: getFileExtension(file.name),
      file,
    }));
    this.files.forEach((f) => {
      this.formGroup.addControl(f.id, new FormControl(f.name, [Validators.required]));
    });
  }

  onFormSubmit(): void {
    if (this.files) {
      const files = this.files.map((uploadFile) => {
        const newFileName = `${this.formGroup.value[uploadFile.id]}.${uploadFile.extension}`;

        return new File([uploadFile.file], newFileName, uploadFile.file);
      });

      this.store$.dispatch(
        CoreActions.uploadMobileFiles({
          customerSpaceId: this.mobileUpload.customerSpaceId,
          mobileUploadId: this.mobileUpload.id,
          fileList: files,
        }),
      );
    }
  }

  onFormReset() {
    this.resetForm();
  }

  private onMobileUploadStatusChange(status: ResourceStatus): void {
    this.uploadPending = status === 'PENDING';

    if (status === 'SUCCEEDED') {
      this.resetForm();
    }
  }

  private resetForm(): void {
    this.files = null;
    Object.keys(this.formGroup.controls).forEach((name) => this.formGroup.removeControl(name));
  }
}
